import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Chip } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { EmptyState, Loading, PageHeader } from '~/components';
import { RelativeTime } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import { CardProgram, CardProgramRepository } from '~/repositories/CardProgramRepository';
import { useSessionStore } from '~/stores/Session';

const TableContainer = styled.div`
  padding: 0 var(--page-margin);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(100)};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: ${({ theme }) => theme.foreground};
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(100)};
`;

const Tr = styled.tr`
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.secondary.blendToBackground(50)};
  }
`;

const StatusChip = styled(Chip)<{ $status: string }>`
  text-transform: capitalize;
`;

const NetworkChip = styled(Chip)`
  text-transform: capitalize;
`;

const TypeChip = styled(Chip)`
  text-transform: capitalize;
`;

const formatNetworkName = (scheme: string): string => {
  if (!scheme) return '-';
  return scheme.toLowerCase().replace(/_/g, ' ');
};

const formatCardProgramType = (type: string): string => {
  if (!type) return '-';
  return type.toLowerCase().replace(/_/g, ' ');
};

const formatStatus = (status: string): string => {
  if (!status) return '-';
  return status.toLowerCase().replace(/_/g, ' ');
};

export const PageCardProgramsOverview: React.FC = () => {
  const navigate = useNavigate();
  const { currentPlatform, isSandbox } = useSessionStore();
  const [cardPrograms, setCardPrograms] = useState<CardProgram[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPrograms = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await CardProgramRepository.listCardPrograms();
        setCardPrograms(response.cardPrograms || []);
      } catch (err) {
        console.error('Error fetching card programs:', err);
        setError('Failed to load card programs');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrograms();
  }, [currentPlatform?.id, isSandbox]);

  const renderTable = () => {
    if (cardPrograms.length === 0) {
      return <EmptyState headline="No card programs" text="No card programs found." />;
    }

    return (
      <Table>
        <thead>
          <tr>
            <Th>Name</Th>
            <Th>Network</Th>
            <Th>BIN</Th>
            <Th>Type</Th>
            <Th>Status</Th>
            <Th>Created</Th>
          </tr>
        </thead>
        <tbody>
          {cardPrograms.map((program) => (
            <Tr key={program.id} onClick={() => navigate(`${ROUTE.CARD_PROGRAMS}/edit/${program.id}`)}>
              <Td>{program.description || program.id}</Td>
              <Td>
                <NetworkChip>{formatNetworkName(program.primaryCardScheme)}</NetworkChip>
              </Td>
              <Td>{program.bin || '-'}</Td>
              <Td>
                <TypeChip>{formatCardProgramType(program.cardProgramType)}</TypeChip>
              </Td>
              <Td>
                <StatusChip $status={program.status}>{formatStatus(program.status)}</StatusChip>
              </Td>
              <Td>{program.createdAt ? <RelativeTime timestamp={program.createdAt} /> : '-'}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    );
  };

  return (
    <>
      <PageHeader text="Card Programs" border={false} />
      <Loading isLoading={isLoading}>
        {error ? <EmptyState headline="Error" text={error} /> : <TableContainer>{renderTable()}</TableContainer>}
      </Loading>
    </>
  );
};
